import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Target, Eye, Heart, ShieldCheck, ArrowRight } from 'lucide-react';
import { aboutData } from '../data/mock';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const valueIcons = [Heart, Target, ShieldCheck, Eye];

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function AboutPage() {
  return (
    <main className="pt-20">

      {/* Header */}
      <section className="py-16 bg-[rgb(17,17,19)] relative">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        />
        <div className="max-w-[1200px] mx-auto px-6 relative">
          <FadeInSection>
            <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">
              Our Story
            </h1>
            <p className="text-[rgb(161,161,170)] text-lg max-w-2xl">
              Since 2013, Scaleup Academy has been on a mission to transform
              education and empower students to achieve their dreams.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[rgb(17,17,19)]">
        <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-2 gap-8">
          <FadeInSection>
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-10 relative">
              <div className="absolute top-0 inset-x-0 h-1 bg-[#F37021]" />
              <div className="w-12 h-12 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center mb-6">
                <Target size={24} className="text-[#F37021]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Our Mission
              </h3>
              <p className="text-[rgb(218,218,218)]">
                {aboutData.mission}
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.1}>
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-10 relative">
              <div className="absolute top-0 inset-x-0 h-1 bg-[#00B4D8]" />
              <div className="w-12 h-12 rounded-xl bg-[rgba(0,180,216,0.15)] flex items-center justify-center mb-6">
                <Eye size={24} className="text-[#00B4D8]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Our Vision
              </h3>
              <p className="text-[rgb(218,218,218)]">
                {aboutData.vision}
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[rgb(26,28,30)]">
        <div className="max-w-[1200px] mx-auto px-6">
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">
                Our Values
              </span>
              <h2 className="text-4xl font-bold text-white mt-3">
                What Drives Us
              </h2>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutData.values.map((val, i) => {
              const Icon = valueIcons[i] || Heart;
              return (
                <FadeInSection key={i} delay={i * 0.1}>
                  <div className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-7 text-center hover:-translate-y-1 transition">
                    <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center">
                      <Icon size={22} className="text-[#F37021]" />
                    </div>
                    <h4 className="text-white font-semibold mb-2">
                      {val.title}
                    </h4>
                    <p className="text-[rgb(161,161,170)] text-sm">
                      {val.desc}
                    </p>
                  </div>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder Message */}
      <section className="py-24 bg-[rgb(17,17,19)]">
        <div className="max-w-[1000px] mx-auto px-6">
          <FadeInSection>
            <div className="text-center mb-14">
              <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">
                Founder’s Note
              </span>
              <h2 className="text-4xl font-bold text-white mt-3">
                A Clearer Way to Learn
              </h2>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.1}>
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-3xl p-10 space-y-8">

              <p className="text-[rgb(218,218,218)] text-lg">
                {aboutData.foundersMessage.intro}
              </p>

              <div className="grid sm:grid-cols-3 gap-3">
                {aboutData.foundersMessage.experienceAreas.map((item, i) => (
                  <div
                    key={i}
                    className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.06)] rounded-lg px-4 py-3 text-[rgb(161,161,170)] text-sm text-center"
                  >
                    {item}
                  </div>
                ))}
              </div>

              <p className="text-[rgb(218,218,218)]">
                {aboutData.foundersMessage.coreObservation}
              </p>

              <p className="text-[#F37021] font-medium">
                {aboutData.foundersMessage.philosophy}
              </p>

              <ul className="space-y-2 text-[rgb(161,161,170)] text-sm">
                {aboutData.foundersMessage.whoWeWorkWith.map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>

              <div className="grid sm:grid-cols-3 gap-4 pt-4">
                {aboutData.foundersMessage.programPrinciples.map((item, i) => (
                  <div
                    key={i}
                    className="bg-[rgba(243,112,33,0.08)] border border-[rgba(243,112,33,0.25)] rounded-xl py-4 text-center text-[#F37021] font-semibold text-sm"
                  >
                    {item}
                  </div>
                ))}
              </div>

              <p className="italic text-[rgb(218,218,218)] pt-4">
                {aboutData.foundersMessage.closingNote}
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-[rgb(17,17,19)]">
        <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {aboutData.team.map((member, i) => (
            <FadeInSection key={i} delay={i * 0.1}>
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-7 text-center hover:-translate-y-1 transition">
                <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[rgba(243,112,33,0.1)] flex items-center justify-center text-[#F37021] font-bold">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <h4 className="text-white font-semibold">
                  {member.name}
                </h4>
                <p className="text-[#F37021] text-sm mb-1">
                  {member.role}
                </p>
                <p className="text-[rgb(161,161,170)] text-xs">
                  {member.exp}
                </p>
              </div>
            </FadeInSection>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[rgb(26,28,30)] text-center">
        <FadeInSection>
          <h2 className="text-4xl font-bold text-white mb-4">
            Join the Scaleup Family
          </h2>
          <p className="text-[rgb(161,161,170)] mb-8">
            Become part of a community that's committed to your success.
          </p>
          <Link to="/contact">
            <Button className="bg-[#F37021] hover:bg-[#DC5A14] text-white rounded-xl px-10 h-14">
              Get In Touch <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </FadeInSection>
      </section>

    </main>
  );
}
